import React, { useContext, useEffect, useState } from "react";

import { TparActive } from "./TparActive";
import { TparHistory } from "./TparHistory";
import TabBasedFeature from "../../../components/layout/TabBasedFeature";
import { UserContext } from "shared/context/UserContext";
import { OrganizationTypes } from "shared/constants/OrganizationTypes";
import ParentCompanySelectionForGroupUsers from "shared/components/parentCompanySelectionForGroupUsers";
import { useFetchParentCompanyAttributes } from "components/hooks/useFetchParentCompanyAttributes";
import { ControlSpinner } from "shared/components/spinners/ControlSpinner";
import TparNoAccessMessage from "./components/TparNoAccessMessage";

export default function Tpar(props) {

    //Test auto deploy Test Branch
    const { passedProps } = props;
    let userContext = React.useContext(UserContext);

    const [
        parentCompanySelectedByGroupUser,
        setParentCompanySelectedByGroupUser,
    ] = useState(null);
    const [membersSelectedByGroupUser, setMembersSelectedByGroupUser] =
        useState(null);
    const [payersSelectedByGroupUser, setPayersSelectedByGroupUser] =
        useState(null);
    const [groupUserHasDeterminedValues, setGroupUserHasDeterminedValues] =
        useState(false);

    const today = new Date();
    const defaultStartDate = new Date();

    //NOTE TODO: Need to set up an API call to get the most recent end date for the reports for a given parent company
    defaultStartDate.setDate(today.getDate() - 25);

    const sendParentCompanyFromGroupUser = (parentCompanyId) => {
        setParentCompanySelectedByGroupUser(parentCompanyId);
    };

    const sendMembersFromGroupUser = (members) => {
        setMembersSelectedByGroupUser(members);
    };

    const sendPayersFromGroupUser = (payers) => {
        setPayersSelectedByGroupUser(payers);
    };

    const resetValuesSelectedByGroupUser = () => {
        setParentCompanySelectedByGroupUser(null);
        setMembersSelectedByGroupUser(null);
        setGroupUserHasDeterminedValues(false);
    };

    useEffect(() => {
        if (userContext && userContext.currentOrganization) {
            resetValuesSelectedByGroupUser();
        }
    }, [userContext && userContext.currentOrganization && userContext.currentOrganization.value]);

    /*
    NOTE: Okay so from here, we can use spread operator to continue passing the existing passedProps, and then add some additional values on top of it
    From here, those props get passed to the TabBasedFeature, and then from there it gets passed to the actual features themselves.
    This is how we can determine the "user determined RPID" and a "user determined set of members".
    */

    // Get the current parent company ID for fetching attributes.
    // NOTE (NETX-5821): Only fall back to currentOrganization.value when the current
    // organization is a ParentCompany. For Group users that value is the GROUP id, not an
    // RPID — sending it to RPID-scoped endpoints is rejected by server-side authorization
    // (403). A group user must explicitly select a parent company first; until then this
    // resolves to null and the RPID-scoped calls are skipped.
    const currentParentCompanyId =
        parentCompanySelectedByGroupUser && parentCompanySelectedByGroupUser.rpid
            ? parentCompanySelectedByGroupUser.rpid
            : userContext &&
              userContext.currentOrganization &&
              userContext.currentOrganization.type === OrganizationTypes.ParentCompany
            ? userContext.currentOrganization.value
            : null;

    // Fetch parent company attributes using the custom hook
    const {
        data: parentCompanyAttributes,
        isLoading: isLoadingAttributes,
        error: attributesError,
        refetch: refetchAttributes
    } = useFetchParentCompanyAttributes(currentParentCompanyId);

    let customPassedProps = {
        ...passedProps,
        parentCompanySetByGroupUser: parentCompanySelectedByGroupUser,
        membersSetByGroupUser: membersSelectedByGroupUser,
        payersSetByGroupUser: payersSelectedByGroupUser,
        resetValuesSelectedByGroupUserCallback: () =>
            resetValuesSelectedByGroupUser(),
        parentCompanyAttributes: parentCompanyAttributes,
        isLoadingAttributes: isLoadingAttributes,
        attributesError: attributesError,
        refetchAttributes: refetchAttributes,
        currentParentCompanyId: currentParentCompanyId,
    };

    useEffect(() => {
        if (
            parentCompanySelectedByGroupUser &&
            membersSelectedByGroupUser.length > 0
        ) {
            setGroupUserHasDeterminedValues(true);
        }
    }, [parentCompanySelectedByGroupUser, membersSelectedByGroupUser]);

    const [
        isCurrentOrganizationAParentCompany,
        setIsCurrentOrganizationAParentCompany,
    ] = useState(false);

    const [currentOrganizationTrigger, setCurrentOrganizationTrigger] =
        useState(false);

    useEffect(() => {
        if (attributesError) {
            console.error("Error loading parent company attributes:", attributesError);
        }
    }, [attributesError]);

    useEffect(() => {
        if (
            userContext !== null &&
            userContext.currentOrganization !== null &&
            userContext.currentOrganization.type !== null
        ) {
            let isParentCompany =
                userContext.currentOrganization.type ===
                OrganizationTypes.ParentCompany;
            setIsCurrentOrganizationAParentCompany(isParentCompany);
        }
    }, [currentOrganizationTrigger]);

    function DetermineCurrentOrganizationTriggerOnPageLoad() {
        if (!userContext || !userContext.currentOrganization) return;

        if (!userContext.currentOrganization.value) return;

        if (currentOrganizationTrigger === userContext.currentOrganization.value)
            return;

        setCurrentOrganizationTrigger(userContext.currentOrganization.value);
    }

    DetermineCurrentOrganizationTriggerOnPageLoad();

    let tabs = [
        { key: "tparActive", title: "Active", Component: TparActive },
        { key: "tparHistory", title: "History", Component: TparHistory },
    ];


    /*
    If there is a value in the parentCompanyAttributes where ABID = 99
    or if there is no value where ABID = 100, then display an error message
     */
    // NOTE (NETX-5821): only spin while we are actually waiting on attributes for a KNOWN
    // parent company. For a Group user who has not selected a parent company yet,
    // currentParentCompanyId is null and the attributes query never runs (it's gated on a
    // truthy id) — so we must fall through to the parent-company selection UI below instead
    // of spinning forever.
    if (currentParentCompanyId && !parentCompanyAttributes) {
        return (
            <div>
                <ControlSpinner />
            </div>
        )
    }
    else if (!isCurrentOrganizationAParentCompany && !groupUserHasDeterminedValues) {
        return (
            <ParentCompanySelectionForGroupUsers
                setParentCompanyCallback={sendParentCompanyFromGroupUser}
                setMembersCallback={sendMembersFromGroupUser}
                setPayersCallback={sendPayersFromGroupUser}
                passedProps={passedProps}
                noAccessContent={<TparNoAccessMessage />}
            />
        );
    }
    else if (!parentCompanyAttributes) {
        // Safety net: a parent-company id is resolved but its attributes are not yet
        // available (e.g. the brief render while switching org from group to a parent
        // company). Spin rather than dereferencing undefined attributes below.
        return (
            <div>
                <ControlSpinner />
            </div>
        )
    }
    else if (parentCompanyAttributes.some(attr => attr.abid === 99) || !parentCompanyAttributes.some(attr => attr.abid === 100)) {
        return <TparNoAccessMessage />;
    }
    else {
        return (
            <TabBasedFeature
                tabs={tabs}
                defaultActiveKey="tparActive"
                passedProps={customPassedProps}
            />
        );
    }
}
